import { useId, useRef } from "react";

import { WorkThumb } from "../lib/WorkThumb.tsx";
import type { Choice } from "../lib/choices.ts";
import { LoadingScreen } from "../lib/Loading.tsx";
import { useModalDialog } from "../lib/useModalDialog.ts";
import { WORK_PICKER_COPY } from "./workPicker.constants.ts";
import { useWorkPickerController } from "./useWorkPickerController.ts";
import type { ResultsView } from "./workPicker.model.ts";

/**
 * Choosing works for a viewing room.
 *
 * A native `<dialog>` opened with `showModal()`, not a hand-rolled overlay, and
 * the reason is everything it does that would otherwise have to be written and
 * got right: modal dialog semantics, Escape, a real focus trap, the rest of the
 * document made INERT rather than merely covered, focus returned to whatever
 * opened it, and the top layer — so there is no portal to mount and no z-index
 * scale to invent.
 *
 * Two things it does not do, both handled here: it does not lock the page
 * behind it (CSS, in viewingRooms.css), and it does not move focus anywhere
 * useful on open — the first focusable element is the close button, so the
 * search box is focused explicitly below.
 *
 * MOUNTED EVEN WHEN SHUT, deliberately. Closing by unmounting would skip
 * `close()`, and it is `close()` that returns focus to the button that opened
 * this. The controller idles on `open`, so a shut dialog fetches nothing.
 *
 * There is no Cancel. Choices apply as they are made — the footer is the running
 * list — so there is nothing to commit and nothing a Cancel could undo. Escape
 * fires an event literally named `cancel`, and if Cancel meant "discard" then a
 * stray Escape would throw away twenty clicks.
 */
export function WorkPickerDialog({
  open,
  selected,
  onToggle,
  onClose,
}: {
  open: boolean;
  selected: Choice[];
  onToggle: (choice: Choice) => void;
  onClose: () => void;
}) {
  const { view, typeQuery, goPrev, goNext } = useWorkPickerController(open, selected);

  const search = useRef<HTMLInputElement>(null);
  const dialog = useModalDialog(
    open,
    // After showModal, never React's autoFocus — that fires at mount, before
    // the dialog is modal, and is then overridden by the browser's own
    // "focus the first focusable thing" pass.
    () => search.current?.focus(),
  );
  const titleId = useId();
  const searchId = useId();
  const hintId = useId();
  const chosenId = useId();

  return (
    <dialog
      ref={dialog}
      className="rooms__picker"
      aria-labelledby={titleId}
      // Escape and the backdrop both close the dialog natively; this is what
      // tells React it happened. Without it the two disagree and the next
      // render calls showModal() on a dialog that is already open.
      onClose={onClose}
    >
      <div className="rooms__picker-head">
        <h2 id={titleId} className="rooms__picker-title">
          {view.title}
        </h2>

        <div className="rooms__field">
          <label className="rooms__label" htmlFor={searchId}>
            {WORK_PICKER_COPY.searchLabel}
          </label>
          <input
            ref={search}
            id={searchId}
            type="search"
            className="rooms__input"
            value={view.query}
            autoComplete="off"
            spellCheck={false}
            aria-describedby={hintId}
            onChange={(event) => typeQuery(event.target.value)}
          />
          <p id={hintId} className="rooms__hint">
            {WORK_PICKER_COPY.searchHint}
          </p>
        </div>

        {/* One region, always in the DOM. Two competing polite regions announce
            nothing coherent, and a region added at the same moment as its
            content is frequently not announced at all. */}
        <p role="status" className="rooms__picker-status">
          {view.announcement}
        </p>
      </div>

      <Results
        results={view.results}
        onToggle={onToggle}
        onPrev={goPrev}
        onNext={goNext}
      />

      <section className="rooms__picker-chosen" aria-labelledby={chosenId}>
        <h3 id={chosenId} className="rooms__legend">
          {view.countLabel}
        </h3>

        {view.selected.length === 0 ? (
          <p className="rooms__hint">{WORK_PICKER_COPY.chosenNone}</p>
        ) : (
          <ul className="chips rooms__picker-chips">
            {view.selected.map((choice) => (
              <li key={choice.key} className="chip chip--static">
                <span>{choice.label}</span>
                <button
                  type="button"
                  className="rooms__picker-remove"
                  // "×" is a glyph, not a name. Every other button on this
                  // screen takes its accessible name from its own text.
                  aria-label={`${WORK_PICKER_COPY.remove} ${choice.label}`}
                  onClick={() => onToggle(choice)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {view.atCapacity && (
          <p className="rooms__hint" role="alert">
            {WORK_PICKER_COPY.atCapacity}
          </p>
        )}

        <div className="rooms__picker-foot">
          <p className="rooms__hint">{WORK_PICKER_COPY.closeNote}</p>
          <button type="button" className="rooms__submit" onClick={onClose}>
            {WORK_PICKER_COPY.done}
          </button>
        </div>
      </section>
    </dialog>
  );
}

function Results({
  results,
  onToggle,
  onPrev,
  onNext,
}: {
  results: ResultsView;
  onToggle: (choice: Choice) => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (results.kind === "first-load") {
    return <LoadingScreen message={results.message} />;
  }

  if (results.kind === "error") {
    return (
      <p className="rooms__error" role="alert">
        {results.message}
      </p>
    );
  }

  if (results.kind === "nothing" || results.kind === "no-matches") {
    return <p className="rooms__note">{results.message}</p>;
  }

  return (
    <div className={`rooms__picker-body${results.busy ? " rooms__picker-body--busy" : ""}`}>
      <ul className="rooms__picker-list">
        {results.rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className={`rooms__picker-row${row.selected ? " rooms__picker-row--on" : ""}`}
              aria-pressed={row.selected}
              disabled={row.disabled}
              onClick={() => onToggle({ key: row.id, label: row.label })}
            >
              {/* Hidden, because WorkThumb's alt is "Photograph of {title}" and
                  the label below says the title again — without this the
                  button's accessible name is the work named twice. */}
              <span className="rooms__picker-thumb" aria-hidden="true">
                <WorkThumb imageUrl={row.imageUrl} title={row.label} />
              </span>
              <span className="rooms__picker-row-text">
                <span className="rooms__picker-row-label">{row.label}</span>
                {row.sublabel && (
                  <span className="rooms__picker-row-sub">{row.sublabel}</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/* Real words on the buttons rather than glyphs with an aria-label, and
          `disabled` rather than aria-disabled — there is nothing to explain
          beyond the end of a list. */}
      <nav className="rooms__picker-pages" aria-label={WORK_PICKER_COPY.pagesLabel}>
        <button
          type="button"
          className="rooms__action"
          disabled={!results.page.canPrev}
          onClick={onPrev}
        >
          {results.page.prevLabel}
        </button>
        <p className="rooms__hint">{results.page.label}</p>
        <button
          type="button"
          className="rooms__action"
          disabled={!results.page.canNext}
          onClick={onNext}
        >
          {results.page.nextLabel}
        </button>
      </nav>
    </div>
  );
}
